// Plan 23b, Task 2.4 — Dice Result Display primitive
//
// Composes: counter-bounce hook (Plan 20a)
//
// Visual structure:
//   Container div `ui-dice-result` — position:fixed, inset:0, flex center, z-index overlay
//     ├── GoldenBurst div `ui-dice-result__burst` — CSS radial-gradient behind number
//     └── Number div `ui-dice-result__number` — displays the die value

import type { UIComponentHandle, UIComponentConfig } from './types';
import type { GsapTimelineHandle } from '../../renderer/vfx/gsap-registry';
import { createCounterBounceHook } from '../vfx/counter-bounce';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/** DiceResultOpts — no required fields; all defaults come from tokens.json.
 *  Defined as a type with an optional placeholder to satisfy no-empty-object-type. */
export type DiceResultOpts = {
  /** Reserved for future per-instance overrides. Currently unused. */
  _?: never;
};

export interface DiceResultAnchor {
  readonly screenX: number;
  readonly screenY: number;
}

export interface DiceResultHandle extends UIComponentHandle {
  /** Set the die value text and trigger the counter-bounce animation. */
  reveal(value: number, anchor?: DiceResultAnchor): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** typography.size.dice-result tuned to match the comic result reference. */
const DICE_RESULT_FONT_SIZE = '148px';
const DICE_RESULT_ANCHORED_FONT_SIZE = '104px';

/** Chunky yellow-orange comic result number. */
const NUMBER_COLOR = '#FFD83D';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/**
 * typography.shadow.title + shadow.glow-purple combined (space-separated)
 * typography.shadow.title   = "0 3px 0 #000000, 0 6px 12px rgba(0,0,0,0.50)"
 * shadow.glow-purple        = "0 0 12px rgba(107,63,160,0.40)"
 */
const NUMBER_TEXT_SHADOW =
  '0 4px 0 #7A2600, 0 7px 0 #2A0B00, 0 10px 18px rgba(0,0,0,0.55), 0 0 12px rgba(255,216,61,0.32)';

/**
 * Tight result halo. Kept deliberately low-alpha so the digit stays dominant.
 */
const BURST_BACKGROUND =
  'radial-gradient(circle, rgba(255,255,255,0.42) 0%, rgba(255,245,208,0.32) 32%, rgba(255,216,61,0.22) 54%, rgba(255,96,24,0.12) 76%, rgba(255,96,24,0) 100%)';

/** Responsive size for the tight result halo. */
const BURST_SIZE = 'clamp(150px, 24vw, 210px)';
const BURST_ANCHORED_SIZE = 'clamp(130px, 20vw, 170px)';
const BURST_BLUR = '6px';

/** z-index.overlay = 350 */
const Z_OVERLAY = 350;

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createDiceResult(config: UIComponentConfig & DiceResultOpts): DiceResultHandle {
  const { gsapRegistry, mount } = config;

  let disposed = false;

  /** In-flight reveal gsap handle; at most one active at a time. */
  let revealHandle: GsapTimelineHandle | null = null;

  // ── Counter-bounce hook ──────────────────────────────────────────────────────

  const bounceHook = createCounterBounceHook({ gsapRegistry });

  // ── Build DOM ────────────────────────────────────────────────────────────────

  // Root container: position:fixed, inset:0, flex center, transparent bg, z-index overlay
  const root = document.createElement('div');
  root.className = 'ui-dice-result';
  root.style.cssText = [
    'position: fixed',
    'top: 0',
    'right: 0',
    'bottom: 0',
    'left: 0',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'background: transparent',
    `z-index: ${Z_OVERLAY}`,
    'pointer-events: none',
    'user-select: none',
  ].join('; ');

  // Golden burst — CSS-only tight halo centered behind the number
  const burstEl = document.createElement('div');
  burstEl.className = 'ui-dice-result__burst';
  burstEl.dataset.burstBackground = BURST_BACKGROUND;
  burstEl.dataset.burstSize = BURST_SIZE;
  burstEl.style.cssText = [
    'position: absolute',
    `--dice-result-burst-size: ${BURST_SIZE}`,
    'width: var(--dice-result-burst-size)',
    'height: var(--dice-result-burst-size)',
    `--dice-result-burst-background: ${BURST_BACKGROUND}`,
    `background: ${BURST_BACKGROUND}`,
    'border-radius: 50%',
    `filter: blur(${BURST_BLUR})`,
    'pointer-events: none',
  ].join('; ');

  // Number element — the die value
  const numberEl = document.createElement('div');
  numberEl.className = 'ui-dice-result__number';
  numberEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${DICE_RESULT_FONT_SIZE}`,
    'font-weight: 900',
    `color: ${NUMBER_COLOR}`,
    `text-shadow: ${NUMBER_TEXT_SHADOW}`,
    'position: relative',
    'z-index: 1',
    'line-height: 1',
    '-webkit-text-stroke: 3px #2a0b00',
    'user-select: none',
  ].join('; ');

  // Assemble DOM: burst (behind number), number
  root.appendChild(burstEl);
  root.appendChild(numberEl);

  if (mount) {
    mount.appendChild(root);
  }

  // ── reveal() ─────────────────────────────────────────────────────────────────

  function reveal(value: number, anchor?: DiceResultAnchor): void {
    if (disposed) return;

    // Kill any prior reveal in-flight
    if (revealHandle) {
      revealHandle.dispose();
      revealHandle = null;
    }

    // Set the number text
    numberEl.textContent = String(value);
    applyPlacement(anchor);

    // Trigger counter-bounce on the number element (delta=0 — no tint for dice)
    const bounceResult = bounceHook.play(numberEl, { delta: 0 });
    revealHandle = bounceResult;
  }

  function applyPlacement(anchor?: DiceResultAnchor): void {
    const anchored =
      anchor != null && Number.isFinite(anchor.screenX) && Number.isFinite(anchor.screenY);

    if (!anchored) {
      delete root.dataset.placement;
      root.style.display = 'flex';
      root.style.alignItems = 'center';
      root.style.justifyContent = 'center';
      burstEl.dataset.burstSize = BURST_SIZE;
      burstEl.style.setProperty('--dice-result-burst-size', BURST_SIZE);
      burstEl.style.left = '';
      burstEl.style.top = '';
      burstEl.style.transform = '';
      numberEl.style.position = 'relative';
      numberEl.style.left = '';
      numberEl.style.top = '';
      numberEl.style.transform = '';
      numberEl.style.fontSize = DICE_RESULT_FONT_SIZE;
      return;
    }

    root.dataset.placement = 'anchored';
    root.style.display = 'block';
    root.style.alignItems = '';
    root.style.justifyContent = '';
    burstEl.dataset.burstSize = BURST_ANCHORED_SIZE;
    burstEl.style.setProperty('--dice-result-burst-size', BURST_ANCHORED_SIZE);
    burstEl.style.left = `${anchor.screenX}px`;
    burstEl.style.top = `${anchor.screenY}px`;
    burstEl.style.transform = 'translate(-50%, -50%)';
    numberEl.style.position = 'absolute';
    numberEl.style.left = `${anchor.screenX}px`;
    numberEl.style.top = `${anchor.screenY}px`;
    numberEl.style.transform = 'translate(-50%, -50%)';
    numberEl.style.fontSize = DICE_RESULT_ANCHORED_FONT_SIZE;
  }

  // ── dispose() ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    // Dispose in-flight reveal handle
    if (revealHandle) {
      revealHandle.dispose();
      revealHandle = null;
    }

    // Dispose bounce hook (kills its internal handle)
    bounceHook.dispose();

    // Detach root from DOM
    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return {
    element: root,
    reveal,
    dispose,
  };
}
